import React, { useState } from 'react';
import { useAuth } from 'react-oidc-context';
import {
  Breadcrumb,
  BreadcrumbItem,
  Content,
  Icon,
  PageBreadcrumb,
  PageSection,
  PageSectionVariants,
  Tab,
  TabTitleText,
  Tabs,
} from '@patternfly/react-core';
import { NewspaperIcon } from '@patternfly/react-icons';
import Unauthenticated from '@app/Unauthenticated';
import { NotFound } from '@app/NotFound/NotFound';
import { useUserContext } from '../../../contexts/user/UserContext';
import NewsletterRecipientsTab from './NewsletterRecipientsTab';
import NewsletterTemplatesTab from './NewsletterTemplatesTab';
import NewslettersTab from './NewslettersTab';

/**
 * Newsletter — the first page of the Ancalagon-staff-only Admin area.
 *
 * Gating is enforced in two independent places (defense in depth):
 *   1. AppLayout hides the whole "Admin" nav group unless `user.is_ancalagon_staff`.
 *   2. This page guards itself: a non-staff user who types `/admin/newsletter` directly
 *      gets <NotFound /> — the same response as a non-existent route, so the admin area's
 *      existence is never leaked. (The backend `/admin` routes enforce this authoritatively;
 *      this is the frontend mirror.)
 */
function Newsletter() {
  const auth = useAuth();
  const user = useUserContext();
  const [activeTab, setActiveTab] = useState<string | number>(0);

  if (!auth.isAuthenticated) {
    return <Unauthenticated />;
  }

  // Ancalagon-staff-only. Treat anything other than an explicit `true` as "no access" and
  // render the 404 page so the route reveals nothing about the admin area's existence.
  if (user?.is_ancalagon_staff !== true) {
    return <NotFound />;
  }

  return (
    <>
      <PageBreadcrumb hasBodyWrapper={false} hasShadowBottom={false}>
        <Breadcrumb ouiaId="BasicBreadcrumb">
          <BreadcrumbItem>Admin</BreadcrumbItem>
          <BreadcrumbItem isActive>Newsletter</BreadcrumbItem>
        </Breadcrumb>
      </PageBreadcrumb>
      <PageSection hasBodyWrapper variant={PageSectionVariants.default} isWidthLimited hasShadowBottom={true}>
        <Content component="h1">
          <Icon size="lg" style={{ marginRight: '8px' }}>
            <NewspaperIcon />
          </Icon>
          Newsletter
        </Content>
        <Content component="p">Compose and send newsletters to Ancalagon customers</Content>
      </PageSection>
      <PageSection hasBodyWrapper={false}>
        <Tabs
          activeKey={activeTab}
          onSelect={(_event, tabKey) => setActiveTab(tabKey)}
          aria-label="Newsletter management tabs"
          mountOnEnter
        >
          <Tab eventKey={0} title={<TabTitleText>Recipients</TabTitleText>}>
            <div className="pf-v6-u-pt-md">
              <NewsletterRecipientsTab />
            </div>
          </Tab>
          <Tab eventKey={1} title={<TabTitleText>Templates</TabTitleText>}>
            <div className="pf-v6-u-pt-md">
              <NewsletterTemplatesTab />
            </div>
          </Tab>
          <Tab eventKey={2} title={<TabTitleText>Newsletters</TabTitleText>}>
            <div className="pf-v6-u-pt-md">
              <NewslettersTab />
            </div>
          </Tab>
        </Tabs>
      </PageSection>
    </>
  );
}

export { Newsletter };
